/**
 * D1 Enterprise Request a Quote — button + modal
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
  var TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
  var toastTimer = null;
  var COLLAPSE_AFTER = 3;
  var variantCache = {};
  var currentLines = [];
  var selectionExpanded = false;
  var advertisedTotalCents = null;

  function getModal() {
    return document.getElementById('D1EnterpriseQuoteModal');
  }

  function money(cents) {
    var value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  function toGid(type, id) {
    var raw = String(id || '').trim();
    if (!raw) return '';
    if (raw.indexOf('gid://') === 0) return raw;
    return 'gid://shopify/' + type + '/' + raw;
  }

  function setTawkVisible(visible) {
    if (window.Tawk_API && typeof window.Tawk_API[visible ? 'showWidget' : 'hideWidget'] === 'function') {
      window.Tawk_API[visible ? 'showWidget' : 'hideWidget']();
    }
  }

  function getProductTitle() {
    var modal = getModal();
    if (modal && modal.dataset.productTitle) return modal.dataset.productTitle;
    var titleEl = document.querySelector(
      'product-info .product__title, .product__title, .product__info-container h1, h1.product__title'
    );
    if (!titleEl) return '';
    return titleEl.textContent.replace(/\s+/g, ' ').trim();
  }

  function getVariants() {
    var modal = getModal();
    if (!modal) return [];
    var el = modal.querySelector('[data-d1-eqr-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  function getSelectedVariantId() {
    var form = document.querySelector('product-form form, form[action*="/cart/add"]');
    if (form) {
      var input = form.querySelector('[name="id"]');
      if (input && input.value) return String(input.value);
    }
    var variantEl = document.querySelector('variant-selects [data-selected-variant], [data-selected-variant]');
    if (variantEl) {
      try {
        var variant = JSON.parse(variantEl.textContent);
        if (variant && variant.id) return String(variant.id);
      } catch (e) {
        /* ignore */
      }
    }
    var variants = getVariants();
    return variants[0] ? String(variants[0].id) : '';
  }

  function getSelectedVariant() {
    var id = getSelectedVariantId();
    var variants = getVariants();
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return variants[0] || null;
  }

  function getSelectedProductLabel() {
    var variant = getSelectedVariant();
    var productTitle = getProductTitle();
    if (!variant) return productTitle || 'this product';
    if (variant.title && variant.title !== 'Default Title') {
      return productTitle ? productTitle + ' — ' + variant.title : variant.title;
    }
    return productTitle || 'this product';
  }

  function getBundleTier() {
    if (window.d1BundleTiers && typeof window.d1BundleTiers.getSelectedTier === 'function') {
      return window.d1BundleTiers.getSelectedTier();
    }
    return null;
  }

  function fetchVariant(id) {
    var key = String(id);
    if (variantCache[key]) return Promise.resolve(variantCache[key]);
    return fetch('/variants/' + key + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Variant not found');
        return r.json();
      })
      .then(function (data) {
        variantCache[key] = data;
        return data;
      });
  }

  function getAdvertisedTotalCents(variant, qty, bundle) {
    var base = Number(variant && variant.price ? variant.price : 0) * qty;
    var hasBundle =
      bundle && bundle.tierId && bundle.tierId !== 'none' && Array.isArray(bundle.accessoryIds) && bundle.accessoryIds.length;
    if (!hasBundle) return base;
    // Same math as sticky ATC: variant price + selected bundle add-on.
    return base + (Number(bundle.bundlePriceCents) || 0);
  }

  function buildQuoteLines() {
    var modal = getModal();
    var productId = modal ? modal.dataset.productId : '';
    var productTitle = getProductTitle();
    var variant = getSelectedVariant();
    if (!variant) {
      advertisedTotalCents = null;
      return Promise.resolve([]);
    }

    var qtyInput = document.querySelector('product-form [name="quantity"], form[action*="/cart/add"] [name="quantity"]');
    var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    var bundle = getBundleTier();
    advertisedTotalCents = getAdvertisedTotalCents(variant, qty, bundle);

    var lines = [
      {
        shopifyProductId: toGid('Product', productId),
        shopifyVariantId: toGid('ProductVariant', variant.id),
        sku: variant.sku || null,
        productTitle: productTitle || 'Product',
        variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : null,
        imageUrl: variant.featured_image || null,
        unitPrice: Number(variant.price || 0) / 100,
        quantity: qty,
        _displayPriceCents: Number(variant.price || 0) * qty,
        _meta: null
      }
    ];

    var hasBundle =
      bundle && bundle.tierId && bundle.tierId !== 'none' && Array.isArray(bundle.accessoryIds) && bundle.accessoryIds.length;

    if (!hasBundle) return Promise.resolve(lines);

    return Promise.all(
      bundle.accessoryIds.map(function (id) {
        return fetchVariant(id).catch(function () {
          return {
            id: id,
            product_id: null,
            name: 'Bundle accessory',
            title: 'Default Title',
            sku: null,
            price: 0,
            featured_image: null
          };
        });
      })
    ).then(function (accessories) {
      accessories.forEach(function (acc, index) {
        var title = acc.name || acc.title || 'Bundle accessory';
        var productOnly = title;
        var variantTitle = null;
        if (acc.name && acc.title && acc.title !== 'Default Title' && acc.name.indexOf(acc.title) !== -1) {
          productOnly = acc.name.replace(/\s+[—\-]\s+.+$/, '').trim() || acc.name;
          variantTitle = acc.title;
        }
        var perPackage =
          bundle.accessoryQuantities && bundle.accessoryQuantities[index] > 0
            ? bundle.accessoryQuantities[index]
            : 1;
        var accQty = perPackage * qty;
        lines.push({
          shopifyProductId: acc.product_id ? toGid('Product', acc.product_id) : null,
          shopifyVariantId: toGid('ProductVariant', acc.id),
          sku: acc.sku || null,
          productTitle: productOnly,
          variantTitle: variantTitle,
          imageUrl: (acc.featured_image && acc.featured_image.src) || null,
          unitPrice: Number(acc.price || 0) / 100,
          quantity: accQty,
          _displayPriceCents: Number(acc.price || 0) * accQty,
          _meta: bundle.tierName || 'Bundle'
        });
      });
      return lines;
    });
  }

  function updateSelectionToggle(lineCount) {
    var modal = getModal();
    if (!modal) return;
    var selection = modal.querySelector('[data-d1-eqr-selection]');
    var toggle = modal.querySelector('[data-d1-eqr-selection-toggle]');
    if (!selection || !toggle) return;

    var overflow = Math.max(0, lineCount - COLLAPSE_AFTER);
    if (overflow <= 0) {
      selection.classList.remove('is-collapsed');
      toggle.setAttribute('hidden', '');
      selectionExpanded = false;
      return;
    }

    toggle.removeAttribute('hidden');
    if (selectionExpanded) {
      selection.classList.remove('is-collapsed');
      toggle.textContent = 'Show fewer items';
    } else {
      selection.classList.add('is-collapsed');
      toggle.textContent = 'Show all ' + lineCount + ' items';
    }
  }

  function renderSelection(lines) {
    var modal = getModal();
    if (!modal) return;
    var selection = modal.querySelector('[data-d1-eqr-selection]');
    var rows = modal.querySelector('[data-d1-eqr-selection-rows]');
    var totalEl = modal.querySelector('[data-d1-eqr-selection-total]');
    var totalLabel = modal.querySelector('[data-d1-eqr-selection-total-label]');
    var wasWrap = modal.querySelector('[data-d1-eqr-selection-was]');
    var wasValue = modal.querySelector('[data-d1-eqr-selection-was-value]');
    if (!rows) return;

    if (!lines.length) {
      rows.innerHTML = '<p class="d1-pmg-selection__meta">Unable to read the current product selection.</p>';
      if (totalEl) totalEl.textContent = '—';
      if (wasWrap) wasWrap.setAttribute('hidden', '');
      updateSelectionToggle(0);
      return;
    }

    rows.innerHTML = lines
      .map(function () {
        return (
          '<div class="d1-pmg-selection__row">' +
          '<div>' +
          '<p class="d1-pmg-selection__name"></p>' +
          '<p class="d1-pmg-selection__meta"></p>' +
          '</div>' +
          '<p class="d1-pmg-selection__price"></p>' +
          '</div>'
        );
      })
      .join('');

    Array.prototype.forEach.call(rows.querySelectorAll('.d1-pmg-selection__row'), function (row, index) {
      var line = lines[index];
      row.querySelector('.d1-pmg-selection__name').textContent = line.productTitle;
      var metaParts = [];
      if (line.variantTitle) metaParts.push(line.variantTitle);
      if (line._meta) metaParts.push(line._meta);
      metaParts.push('Qty ' + line.quantity);
      row.querySelector('.d1-pmg-selection__meta').textContent = metaParts.join(' · ');
      row.querySelector('.d1-pmg-selection__price').textContent =
        line._displayPriceCents > 0 ? money(line._displayPriceCents) : '—';
      if (index >= COLLAPSE_AFTER) row.classList.add('is-overflow');
    });

    var retailCents = lines.reduce(function (sum, line) {
      return sum + (Number(line._displayPriceCents) || 0);
    }, 0);
    var packageCents =
      advertisedTotalCents != null && advertisedTotalCents > 0 ? advertisedTotalCents : retailCents;

    if (totalEl) totalEl.textContent = money(packageCents);
    if (totalLabel) totalLabel.textContent = packageCents < retailCents ? 'Package total' : 'Total';

    if (wasWrap && wasValue) {
      if (packageCents < retailCents) {
        wasWrap.removeAttribute('hidden');
        wasValue.textContent = money(retailCents);
      } else {
        wasWrap.setAttribute('hidden', '');
      }
    }

    if (selection) selection.dataset.packageTotalCents = String(packageCents);
    updateSelectionToggle(lines.length);
  }

  function refreshSelection() {
    return buildQuoteLines().then(function (lines) {
      currentLines = lines;
      renderSelection(lines);
      return lines;
    });
  }

  function showToast(message) {
    var existing = document.getElementById('D1EnterpriseQuoteToast');
    if (existing) existing.remove();
    if (toastTimer) clearTimeout(toastTimer);

    var toast = document.createElement('div');
    toast.id = 'D1EnterpriseQuoteToast';
    toast.className = 'd1-pmg-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 250);
    }, 4200);
  }

  function buildEnquiryMessage() {
    return "Hi, I'm looking to price match " + getSelectedProductLabel();
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(
        function () {
          return true;
        },
        function () {
          return legacyCopy(text);
        }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    try {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.top = '-9999px';
      document.body.appendChild(area);
      area.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(area);
      return !!ok;
    } catch (e) {
      return false;
    }
  }

  function setTawkEnquiryAttribute(message, productLabel) {
    if (!window.Tawk_API || typeof window.Tawk_API.setAttributes !== 'function') return;
    window.Tawk_API.setAttributes(
      {
        'price-match-product': String(productLabel).slice(0, 255),
        'price-match-enquiry': String(message).slice(0, 255)
      },
      function () {
        /* ignore */
      }
    );
  }

  function maximizeTawk() {
    if (window.Tawk_API && typeof window.Tawk_API.maximize === 'function') {
      window.Tawk_API.maximize();
      return true;
    }
    return false;
  }

  function openLiveChat() {
    var message = buildEnquiryMessage();
    var productLabel = getSelectedProductLabel();

    closeModal();
    setTawkVisible(true);
    setTawkEnquiryAttribute(message, productLabel);

    copyText(message).then(function (copied) {
      if (copied) {
        var pasteHint =
          /Mac|iPhone|iPad|iPod/.test(navigator.platform || '') ||
          (navigator.userAgentData && navigator.userAgentData.platform === 'macOS')
            ? '⌘V'
            : 'Ctrl+V';
        showToast('Enquiry copied — paste in chat with ' + pasteHint);
      }
    });

    if (maximizeTawk()) return;
    if (window.Tawk_API) {
      var prevOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = function () {
        if (typeof prevOnLoad === 'function') prevOnLoad();
        setTawkEnquiryAttribute(message, productLabel);
        maximizeTawk();
      };
    }
    setTimeout(maximizeTawk, 400);
  }

  function resolveEndpointUrl(url) {
    var value = String(url || '').trim();
    if (!value) return '';
    var isLocalThemePreview = ['localhost', '127.0.0.1'].indexOf(window.location.hostname) !== -1;
    if (!isLocalThemePreview) return value;
    try {
      var endpoint = new URL(value);
      if (endpoint.hostname === 'd1x.d1store.app') {
        endpoint.protocol = 'http:';
        endpoint.hostname = '127.0.0.1';
        endpoint.port = '4000';
        return endpoint.toString();
      }
    } catch (e) {
      return value;
    }
    return value;
  }

  function formatApiError(payload) {
    var base = (payload && payload.error && payload.error.message) || 'Network response was not ok';
    var details = payload && payload.error && payload.error.details;
    var fieldErrors = details && details.fieldErrors;
    if (!fieldErrors || typeof fieldErrors !== 'object') return base;
    var messages = Object.keys(fieldErrors)
      .map(function (field) {
        var errors = fieldErrors[field];
        return Array.isArray(errors) ? errors.map(function (error) { return field + ': ' + error; }).join('; ') : '';
      })
      .filter(Boolean);
    return messages.length ? base + ' (' + messages.join('; ') + ')' : base;
  }

  var turnstileLoadPromise = null;

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileLoadPromise) return turnstileLoadPromise;
    turnstileLoadPromise = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + TURNSTILE_SCRIPT_SRC + '"]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Failed to load Turnstile')); });
        if (window.turnstile) resolve();
        return;
      }
      var script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('Failed to load Turnstile')); };
      document.head.appendChild(script);
    });
    return turnstileLoadPromise;
  }

  function getTurnstileToken(modal) {
    return String(modal.__d1PmgTurnstileToken || '').trim();
  }

  function resetTurnstile(modal) {
    modal.__d1PmgTurnstileToken = '';
    if (modal.__d1PmgTurnstileWidgetId != null && window.turnstile) {
      window.turnstile.reset(modal.__d1PmgTurnstileWidgetId);
    }
  }

  function initTurnstile(modal) {
    var siteKey = String(modal.dataset.turnstileSiteKey || '').trim();
    var container = modal.querySelector('[data-d1-eqr-turnstile]');
    if (!siteKey || !container) return Promise.resolve();

    return loadTurnstileScript()
      .then(function () {
        if (!window.turnstile) return;
        if (modal.__d1PmgTurnstileWidgetId != null) {
          window.turnstile.remove(modal.__d1PmgTurnstileWidgetId);
        }
        modal.__d1PmgTurnstileToken = '';
        modal.__d1PmgTurnstileWidgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: 'light',
          callback: function (token) {
            modal.__d1PmgTurnstileToken = token;
          },
          'expired-callback': function () {
            modal.__d1PmgTurnstileToken = '';
          },
          'error-callback': function () {
            modal.__d1PmgTurnstileToken = '';
          },
          'timeout-callback': function () {
            modal.__d1PmgTurnstileToken = '';
          }
        });
      })
      .catch(function (error) {
        console.warn('Turnstile failed to load', error);
      });
  }

  function openModal() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTawkVisible(false);
    refreshSelection();
    if (!modal.__d1PmgTurnstileReady) {
      modal.__d1PmgTurnstileReady = true;
      initTurnstile(modal);
    }
    var closeBtn = modal.querySelector('[data-d1-eqr-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTawkVisible(true);
  }

  function submitQuote(modal) {
    var form = modal.querySelector('[data-d1-eqr-form]');
    var status = modal.querySelector('[data-d1-eqr-status]');
    var submitBtn = modal.querySelector('[data-d1-eqr-submit]');
    var setStatus = function (message) {
      if (status) status.textContent = message || '';
    };

    if (!form.checkValidity()) {
      form.reportValidity();
      setStatus('Please complete the required fields.');
      return;
    }

    var honeypot = form.querySelector('[data-honeypot-field]');
    if (honeypot && honeypot.value.trim() !== '') return;

    var siteKey = String(modal.dataset.turnstileSiteKey || '').trim();
    var turnstileToken = getTurnstileToken(modal);
    if (siteKey && !turnstileToken) {
      setStatus('Please complete the human verification before submitting.');
      return;
    }

    var submitUrl = resolveEndpointUrl(modal.dataset.submitUrl);
    if (!submitUrl) {
      setStatus('Quote submission is not configured.');
      return;
    }

    if (modal.dataset.submitting === 'true') return;
    modal.dataset.submitting = 'true';
    var originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';
    }
    setStatus('Sending your request…');

    refreshSelection()
      .then(function (lines) {
        if (!lines.length) throw new Error('No products selected.');

        var companyName = String(form.querySelector('[data-d1-eqr-company]').value || '').trim();

        var notes = String(form.querySelector('[data-d1-eqr-notes]').value || '').trim();
        var packageNote =
          advertisedTotalCents != null && advertisedTotalCents > 0
            ? '\nAdvertised package total: ' + money(advertisedTotalCents)
            : '';
        var prefixedNotes = notes
          ? 'Enterprise quote request' + packageNote + '\n\n' + notes
          : 'Enterprise quote request' + packageNote;

        var payload = {
          honeypot: '',
          shopifyCustomerId: form.querySelector('[data-d1-eqr-customer-id]')
            ? form.querySelector('[data-d1-eqr-customer-id]').value
            : '',
          shopifyCustomerEmail: form.querySelector('[data-d1-eqr-email]').value.trim(),
          customerName: form.querySelector('[data-d1-eqr-name]').value.trim(),
          companyName: companyName,
          customerPhone: form.querySelector('[data-d1-eqr-phone]').value.trim(),
          customerNotes: prefixedNotes,
          marketingOptIn: false,
          turnstileToken: turnstileToken,
          lines: lines.map(function (line) {
            return {
              shopifyProductId: line.shopifyProductId,
              shopifyVariantId: line.shopifyVariantId,
              sku: line.sku,
              productTitle: line.productTitle,
              variantTitle: line.variantTitle,
              imageUrl: line.imageUrl,
              unitPrice: line.unitPrice,
              quantity: line.quantity
            };
          })
        };

        return fetch(submitUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (response) {
          return response.json().catch(function () { return null; }).then(function (body) {
            if (!response.ok) throw new Error(formatApiError(body));
            return body;
          });
        });
      })
      .then(function (body) {
        var data = (body && body.data) || {};
        var panelHeading = modal.querySelector('[data-d1-eqr-panel-heading]');
        var panelSub = modal.querySelector('[data-d1-eqr-panel-sub]');
        var selection = modal.querySelector('[data-d1-eqr-selection]');
        var success = modal.querySelector('[data-d1-eqr-success]');
        var refEl = modal.querySelector('[data-d1-eqr-success-ref]');
        var quoteNumber = data.quoteNumber || data.quote_number || '';
        var requestReference = data.requestReference || data.request_reference || '';
        var displayNumber = quoteNumber || requestReference;

        form.setAttribute('hidden', '');
        if (panelHeading) panelHeading.setAttribute('hidden', '');
        if (panelSub) panelSub.setAttribute('hidden', '');
        if (selection) selection.setAttribute('hidden', '');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText || 'Send request';
        }
        if (success) {
          success.removeAttribute('hidden');
          if (refEl) {
            if (displayNumber) {
              refEl.removeAttribute('hidden');
              refEl.innerHTML = (quoteNumber ? 'Quote number' : 'Reference') + ' <strong></strong>';
              refEl.querySelector('strong').textContent = displayNumber;
            } else {
              refEl.setAttribute('hidden', '');
            }
          }
        }
        setStatus('');
        modal.dataset.submitting = 'false';
      })
      .catch(function (error) {
        console.error('Enterprise quote submit failed', error);
        resetTurnstile(modal);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText || 'Send request';
        }
        setStatus(error.message || 'Submission failed. Please try again.');
        modal.dataset.submitting = 'false';
      });
  }

  function onKeydown(event) {
    var modal = getModal();
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== 'Tab') return;
    var focusables = Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function init() {
    if (!getModal()) return;

    document.addEventListener('click', function (event) {
      var openTrigger = event.target.closest('[data-d1-eqr-open]');
      if (openTrigger) {
        event.preventDefault();
        openModal();
        return;
      }

      if (event.target.closest('[data-d1-eqr-close]')) {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.target.closest('[data-d1-eqr-chat]')) {
        event.preventDefault();
        openLiveChat();
      }
    });

    document.addEventListener('change', function (event) {
      if (!event.target.closest('[data-d1-bundle-tier-input], variant-selects, [name="id"], [name="quantity"]')) return;
      var modal = getModal();
      if (modal && modal.getAttribute('aria-hidden') === 'false') refreshSelection();
    });

    document.addEventListener('keydown', onKeydown);

    var modal = getModal();
    var form = modal && modal.querySelector('[data-d1-eqr-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        submitQuote(modal);
      });
    }

    var toggle = modal && modal.querySelector('[data-d1-eqr-selection-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        selectionExpanded = !selectionExpanded;
        updateSelectionToggle(currentLines.length);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.d1EnterpriseQuoteRequest = {
    open: openModal,
    close: closeModal,
    openLiveChat: openLiveChat,
    buildEnquiryMessage: buildEnquiryMessage,
    refreshSelection: refreshSelection
  };
})();
